import { _x } from '@wordpress/i18n'
import { TranslatedHighlight } from '../../../../assets/utils/algolia/algoliaTranslation'
import type { HitComponentTemplateProps } from './HitComponent'
import { useConfigContext } from './ConfigContext'

export default function ListHitComponent({
  hit,
  isNew,
  isGlobal,
  isRegional,
  regionalOrganisationName,
  isPermanent,
  isCancelled,
  tags,
  location,
  date,
  time,
  detailUrl,
  detailLinkProps,
  ctaButtonText,
  ctaButtonUrl,
  ctaLinkProps,
  teaser,
}: Omit<HitComponentTemplateProps, 'imageUrl'>) {
  const { typePreFilter } = useConfigContext()
  // Offers-only lists make the "Ongoing Offer" badge redundant on every row — hide it there.
  const showPermanentBadge = isPermanent && typePreFilter !== 'offers'
  const metaTemplate = (
    <div className="card-meta flex-column flex-lg-row align-items-start align-items-lg-center">
      {!!date && !isPermanent && (
        <div className="card-meta-tag">
          <i className="icon fa-calendar-days" />
          <span className={isCancelled ? 'text-decoration-line-through' : undefined}>{date}</span>
        </div>
      )}
      {!!time && !isPermanent && (
        <div className="card-meta-tag">
          <i className="icon fa-clock" />
          <span className={isCancelled ? 'text-decoration-line-through' : undefined}>{time}</span>
        </div>
      )}
      {!!location && (
        <div className="card-meta-tag">
          <i className="icon fa-location-dot" />
          <span>{location}</span>
        </div>
      )}
    </div>
  )

  return (
    <article
      data-bs-theme="dark"
      className="card"
      style={{
        viewTransitionName: `card-${hit.objectID}`
      }}
    >
      <div className="card-body p-3 p-lg-5">
        {(isNew ||
          isGlobal ||
          isRegional ||
          showPermanentBadge ||
          !!tags.length ||
          !!date ||
          !!location) && (
          <div className="d-flex justify-content-between align-items-start mb-4 gap-2 flex-wrap">
            <div className="d-lg-none">{metaTemplate}</div>
            <div className="d-flex gap-2">
              {isNew && (
                <div className="badge text-bg-primary">
                  {_x('NEW', 'New Tag', 'icf')}
                </div>
              )}
              {isGlobal && (
                <div className="badge text-bg-primary2">
                  {_x('ICF Movement', 'ICF Movement Tag', 'icf')}
                </div>
              )}
              {isRegional && (
                <div className="badge text-bg-trend3">
                  {regionalOrganisationName}
                </div>
              )}
              {isCancelled && (
                <div className="badge text-bg-danger">
                  {_x('Cancelled', 'Event Occurrence Cancelled Badge', 'icf')}
                </div>
              )}
              {showPermanentBadge && (
                <div className="badge d-inline-flex text-bg-white align-items-center gap-1">
                  <i className="icon fa-rotate" />
                  {_x('Ongoing Offer', 'Event Tag', 'icf')}
                </div>
              )}
              {tags.map((tag) => (
                <div
                  className="badge d-none d-lg-inline-block text-white bg-dark-1"
                  key={tag}
                >
                  {tag}
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="row">
          <div className="col-12 col-lg-4">
            <div className="d-flex flex-column h-100">
              <h3 className="mb-4 mb-lg-3">
                <TranslatedHighlight hit={hit} attribute="title" />
              </h3>
              <div className="d-none d-lg-block mt-auto">{metaTemplate}</div>
            </div>
          </div>
          <div className="col-5 d-none d-lg-block">
            <p className="fs-sm">
              {teaser}
            </p>
          </div>
          <div className="col-12 col-lg-3">
            <div className="d-flex flex-lg-column w-fit-content ms-lg-auto flex-wrap gap-1">
              <a
                href={detailUrl}
                {...detailLinkProps}
                className="btn events-list-button card-stretched-link"
              >
                <span>
                  {_x(
                    'Learn more',
                    'Events List Card Detail Link Button',
                    'icf'
                  )}
                </span>
                <i className="icon fa-arrow-right" />
              </a>
              {!!(ctaButtonText && ctaButtonUrl) && (
                <a href={ctaButtonUrl} {...ctaLinkProps} className="btn events-list-button z-1">
                  <span>{ctaButtonText}</span>
                  <i className="icon fa-arrow-right" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  )
}
